import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  CircleAlert,
  ExternalLink,
  Loader2,
  Pencil,
  Play,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  providersApi,
  type ProviderCredential,
} from "@/features/settings/api";

const KEY = ["providers"] as const;

type ProviderCategory = "STT" | "LLM" | "TTS" | "Telephony";

const CATEGORY_ORDER: ProviderCategory[] = ["STT", "LLM", "TTS", "Telephony"];

const CATEGORY_INFO: Record<ProviderCategory, { title: string; blurb: string }> =
  {
    STT: {
      title: "Speech-to-Text (STT)",
      blurb:
        "Transcribes the caller's voice in real time. Pick the engine per agent on its Configuration tab.",
    },
    LLM: {
      title: "Language Models (LLM)",
      blurb: "Powers the agent's conversation.",
    },
    TTS: {
      title: "Text-to-Speech (TTS)",
      blurb: "Gives the agent its voice.",
    },
    Telephony: {
      title: "Telephony",
      blurb: "Inbound / outbound calling and phone-number management.",
    },
  };

const PROVIDER_INFO: Record<
  string,
  {
    name: string;
    description: string;
    placeholder: string;
    category: ProviderCategory;
    endpointLabel?: string;
    endpointPlaceholder?: string;
    endpointHelp?: string;
    /** Console page where the key lives. */
    keyUrl?: string;
    /** One-line "where to find it" instructions shown on the card + dialog. */
    keyHelp?: string;
  }
> = {
  deepgram: {
    name: "Deepgram",
    description: "Speech-to-Text — real-time caller voice to text (default STT)",
    placeholder: "dg-...",
    category: "STT",
    keyUrl: "https://console.deepgram.com/",
    keyHelp: "Sign in → API Keys → Create a New API Key.",
  },
  assemblyai: {
    name: "AssemblyAI",
    description:
      "Speech-to-Text — Universal-Streaming, native telephony audio, ~300ms",
    placeholder: "API key",
    category: "STT",
    keyUrl: "https://www.assemblyai.com/app/api-keys",
    keyHelp: "Sign in → your API key is on the dashboard (API Keys page).",
  },
  gladia: {
    name: "Gladia",
    description:
      "Speech-to-Text — real-time, strongest for mid-call language switching",
    placeholder: "API key",
    category: "STT",
    keyUrl: "https://app.gladia.io/account",
    keyHelp: "Sign in → Account → copy your API key.",
  },
  openai: {
    name: "OpenAI",
    description: "LLM conversations (GPT-4o, GPT-4o-mini) + TTS fallback",
    placeholder: "sk-...",
    category: "LLM",
    keyUrl: "https://platform.openai.com/api-keys",
    keyHelp: "Sign in → API keys → Create new secret key (starts with sk-).",
  },
  anthropic: {
    name: "Anthropic",
    description: "Claude (Haiku/Sonnet/Opus) — faster TTFT than GPT-4o",
    placeholder: "sk-ant-...",
    category: "LLM",
    keyUrl: "https://console.anthropic.com/settings/keys",
    keyHelp: "Sign in → Settings → API keys → Create Key (starts with sk-ant-).",
  },
  groq: {
    name: "Groq",
    description: "GPT-OSS on Groq LPU — sub-500ms TTFT, fastest LLM for voice",
    placeholder: "gsk_...",
    category: "LLM",
    keyUrl: "https://console.groq.com/keys",
    keyHelp: "Sign in → API Keys → Create API Key (starts with gsk_).",
  },
  elevenlabs: {
    name: "ElevenLabs",
    description: "Text-to-Speech — natural-sounding agent voice",
    placeholder: "xi-...",
    category: "TTS",
    keyUrl: "https://elevenlabs.io/app/settings/api-keys",
    keyHelp: "Sign in → profile icon → API Keys → Create key.",
  },
  cartesia: {
    name: "Cartesia",
    description: "TTS (Sonic-2) — sub-100ms TTFB, used by v3 pipeline",
    placeholder: "sk_car_...",
    category: "TTS",
    keyUrl: "https://play.cartesia.ai/keys",
    keyHelp: "Sign in → API Keys → New API key.",
  },
  twilio: {
    name: "Twilio",
    description: "Telephony — inbound/outbound calls + phone number management",
    placeholder: "Auth Token",
    category: "Telephony",
    keyUrl: "https://console.twilio.com/",
    keyHelp: "Console home → Account Info box: copy the LIVE Account SID (AC…) and Auth Token — not the Test credentials, and not an 'API key'. Use the Twilio account that owns your phone numbers; a subaccount has its own SID + token.",
    endpointLabel: "Account SID",
    endpointPlaceholder: "ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
    endpointHelp: "Your Twilio Account SID (starts with AC)",
  },
  telnyx: {
    name: "Telnyx",
    description: "Inbound + outbound calls via Call Control API",
    placeholder: "KEY...",
    category: "Telephony",
    keyUrl: "https://portal.telnyx.com/#/app/api-keys",
    keyHelp: "Mission Control → Auth → API Keys. The Connection ID comes from Voice → Programmable Voice → your application.",
    endpointLabel: "Connection ID",
    endpointPlaceholder: "e.g. 2512893048709876543",
    endpointHelp:
      "Numeric ID of your Telnyx Voice API Application (Call Control). " +
      "Required for outbound calling and voicemail detection.",
  },
  vonage: {
    name: "Vonage",
    description: "SIP Trunking — inbound calls via Vonage / Nexmo API",
    placeholder: "API Secret",
    category: "Telephony",
    keyUrl: "https://dashboard.nexmo.com/settings",
    keyHelp: "Dashboard → Settings: copy the API key (goes in the API Key field) and API secret (goes in the key field).",
    endpointLabel: "API Key",
    endpointPlaceholder: "",
    endpointHelp: "Your Vonage API Key",
  },
};

const PROVIDER_TYPES = Object.keys(PROVIDER_INFO);
// Providers grouped by category, in display order.
const PROVIDERS_BY_CATEGORY = CATEGORY_ORDER.map((cat) => ({
  category: cat,
  types: PROVIDER_TYPES.filter((t) => PROVIDER_INFO[t].category === cat),
})).filter((g) => g.types.length > 0);

export default function ProvidersTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: [...KEY, "list"],
    queryFn: providersApi.list,
    staleTime: 30_000,
  });
  const credentials = q.data ?? [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ProviderCredential | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProviderCredential | null>(
    null,
  );

  function invalidate() {
    qc.invalidateQueries({ queryKey: [...KEY, "list"] });
  }

  function openAdd() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(c: ProviderCredential) {
    setEditing(c);
    setDialogOpen(true);
  }

  const testM = useMutation({
    mutationFn: (id: string) => providersApi.test(id),
    onMutate: (id) => setTestingId(id),
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.detail || "Connection successful");
      } else {
        toast.error(data.detail || "Connection failed");
      }
      invalidate();
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error || "Test failed");
    },
    onSettled: () => setTestingId(null),
  });

  const removeM = useMutation({
    mutationFn: (id: string) => providersApi.remove(id),
    onSuccess: () => {
      toast.success("Provider removed");
      invalidate();
    },
    onError: () => toast.error("Failed to remove"),
  });

  if (q.isLoading) {
    return <Skeleton className="h-40 w-full" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">API Providers</h2>
          <p className="text-sm text-muted-foreground">
            Configure your API keys for LLM, STT, and TTS providers.
          </p>
        </div>
        <Button onClick={openAdd}>
          <Plus className="size-4" />
          Add Provider Key
        </Button>
      </div>

      {credentials.length === 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          No API keys configured. Agents cannot function without at least an
          OpenAI key.
        </div>
      )}

      {PROVIDERS_BY_CATEGORY.map(({ category, types }) => (
        <section key={category} className="space-y-3 pt-6 first:pt-0">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="shrink-0 text-sm font-bold uppercase tracking-wide text-foreground">
                {CATEGORY_INFO[category].title}
              </h3>
              <div className="h-px flex-1 bg-border" />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {CATEGORY_INFO[category].blurb}
            </p>
          </div>
          {types.map((type) => {
            const info = PROVIDER_INFO[type];
            const creds = credentials.filter((c) => c.provider === type);
            return (
          <Card key={type}>
            <CardHeader className="flex-row items-start justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base">{info.name}</CardTitle>
                <CardDescription>{info.description}</CardDescription>
                {info.keyUrl && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    <a
                      href={info.keyUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-primary underline underline-offset-2"
                    >
                      Where to get your {info.category === "Telephony" ? "credentials" : "key"}
                      <ExternalLink className="size-3" />
                    </a>
                    {info.keyHelp && <> — {info.keyHelp}</>}
                  </p>
                )}
              </div>
              {creds.length === 0 && (
                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  Not configured
                </span>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              {creds.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-3 rounded-md bg-muted/40 px-3 py-2"
                >
                  {c.is_active ? (
                    <CheckCircle2 className="size-4 shrink-0 text-emerald-500" />
                  ) : (
                    <CircleAlert className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {c.label || c.api_key_prefix}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      Key: {c.api_key_prefix}
                      {c.last_tested_at && (
                        <>
                          {" · "}Tested:{" "}
                          {new Date(c.last_tested_at).toLocaleDateString()}
                        </>
                      )}
                      {c.last_error && (
                        <span className="text-rose-600 dark:text-rose-400">
                          {" · "}Error: {c.last_error}
                        </span>
                      )}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={() => testM.mutate(c.id)}
                    disabled={testingId === c.id}
                    aria-label="Test connection"
                  >
                    {testingId === c.id ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Play className="size-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={() => openEdit(c)}
                    aria-label="Edit"
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 text-destructive hover:text-destructive"
                    onClick={() => setDeleteTarget(c)}
                    aria-label="Remove"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
            );
          })}
        </section>
      ))}

      <ProviderDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={invalidate}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove provider credential</AlertDialogTitle>
            <AlertDialogDescription>
              Remove this credential for{" "}
              <span className="font-medium text-foreground">
                {deleteTarget?.label || deleteTarget?.api_key_prefix}
              </span>
              ? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) removeM.mutate(deleteTarget.id);
                setDeleteTarget(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProviderDialog({
  open,
  onOpenChange,
  editing,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: ProviderCredential | null;
  onSaved: () => void;
}) {
  const [provider, setProvider] = useState("openai");
  const [label, setLabel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [endpoint, setEndpoint] = useState("");

  useEffect(() => {
    if (open) {
      if (editing) {
        setProvider(editing.provider);
        setLabel(editing.label || "");
        setApiKey("");
        setEndpoint(editing.api_endpoint || "");
      } else {
        setProvider("openai");
        setLabel("");
        setApiKey("");
        setEndpoint("");
      }
    }
  }, [open, editing]);

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        const data: {
          label?: string;
          api_endpoint?: string;
          api_key?: string;
        } = {
          label,
          api_endpoint: endpoint,
        };
        if (apiKey) data.api_key = apiKey;
        await providersApi.update(editing.id, data);
      } else {
        await providersApi.create({
          provider,
          label,
          api_key: apiKey,
          api_endpoint: endpoint,
        });
      }
    },
    onSuccess: () => {
      toast.success(editing ? "Provider updated" : "Provider added");
      onSaved();
      onOpenChange(false);
    },
    onError: (err) => {
      const e = err as { response?: { data?: Record<string, unknown> } };
      const data = e?.response?.data;
      const msg =
        (typeof data?.error === "string" && data.error) ||
        (typeof data?.message === "string" && data.message) ||
        (Array.isArray((data as { api_key?: string[] })?.api_key) &&
          (data as { api_key: string[] }).api_key[0]) ||
        "Failed to save";
      toast.error(msg);
    },
  });

  const info = PROVIDER_INFO[provider];
  const isTwilio = provider === "twilio";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Update Provider Key" : "Add Provider Key"}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          autoComplete="off"
          className="space-y-3"
        >
          <div className="space-y-1.5">
            <Label>Provider</Label>
            <Select
              value={provider}
              onValueChange={setProvider}
              disabled={!!editing}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROVIDERS_BY_CATEGORY.map(({ category, types }) => (
                  <SelectGroup key={category}>
                    <SelectLabel>{CATEGORY_INFO[category].title}</SelectLabel>
                    {types.map((p) => (
                      <SelectItem key={p} value={p}>
                        {PROVIDER_INFO[p].name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="provider-label">Label (optional)</Label>
            <Input
              id="provider-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Production Key"
              autoComplete="off"
            />
          </div>

          {info.keyUrl && (
            <div className="rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
              <a
                href={info.keyUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium text-primary underline underline-offset-2"
              >
                Open {info.name} console
                <ExternalLink className="size-3" />
              </a>
              {info.keyHelp && <span> — {info.keyHelp}</span>}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="provider-key">
              {isTwilio ? "Auth Token" : "API Key"}
            </Label>
            <Input
              id="provider-key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={info.placeholder}
              autoComplete="new-password"
              required={!editing}
            />
            <p className="text-xs text-muted-foreground">
              {editing
                ? "Leave blank to keep existing key"
                : "Your key is encrypted at rest"}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="provider-endpoint">
              {info.endpointLabel || "Custom API Endpoint (optional)"}
            </Label>
            <Input
              id="provider-endpoint"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              placeholder={
                info.endpointPlaceholder || "https://api.openai.com/v1"
              }
              required={isTwilio}
            />
            <p className="text-xs text-muted-foreground">
              {info.endpointHelp ||
                "Only needed for proxy endpoints or custom LLM servers"}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={save.isPending || (!editing && !apiKey)}
            >
              {save.isPending ? "Saving…" : editing ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
